/**
 * ChartDrawLayer.js
 *
 * SVG rendering layer for chart drawings.
 *
 * WHY THIS EXISTS:
 * - Drawings need to be rendered as SVG elements inside the Recharts chart
 * - Uses Recharts scales to convert data coordinates back to pixels
 * - Renders both completed drawings and the in-progress preview
 *
 * COORDINATE SYSTEM:
 * - Drawings are stored in data coordinates (timestamp, price value)
 * - This component converts them to pixels using Recharts axis scales
 * - This allows drawings to survive zoom/resize operations
 *
 * HOVER-TO-DELETE (2026-02-04):
 * - Shows a delete button when hovering over a drawing
 * - Clicking the delete button removes that specific drawing
 * - Only works when NOT in draw mode (to prevent accidental deletes while drawing)
 *
 * HOVER DETECTION VIA COORDINATES (2026-02-04):
 * - SVG hover events don't work inside Recharts Customized components
 * - Recharts intercepts mouse events at the chart level before they reach SVG elements
 * - Solution: Hover is detected in the parent (IndicatorChart) via pixel coordinates
 * - Parent calculates which drawing is under the mouse using findDrawingAtPoint()
 * - hoveredDrawingId is passed as a prop, not managed internally
 * - Delete button has pointer-events: auto to be clickable
 */
import React from 'react';

/**
 * Render a single drawing shape based on its type.
 *
 * @param {Object} drawing - Drawing object with type, start, end, color
 * @param {Function} xScale - X-axis scale function
 * @param {Function} yScale - Y-axis scale function
 * @param {boolean} isPreview - Whether this is an in-progress preview
 * @returns {React.Element|null} SVG element for the drawing
 */
const renderDrawing = (drawing, xScale, yScale, isPreview = false) => {
  const { id, type, start, end, color } = drawing;

  // Convert data coordinates to pixel coordinates
  const x1 = xScale(start.timestamp);
  const y1 = yScale(start.value);
  const x2 = xScale(end.timestamp);
  const y2 = yScale(end.value);

  // Validate coordinates
  if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) {
    return null;
  }

  // Preview drawings are semi-transparent
  const opacity = isPreview ? 0.6 : 1;
  const strokeWidth = 2;
  const strokeColor = color;

  switch (type) {
    case 'pin': {
      // Pin: A marker dot at a single point
      // Outer circle with inner dot for visibility
      return (
        <g key={id} className="chart-drawing chart-drawing-pin">
          {/* Invisible hit area - larger circle for easier hover detection */}
          <circle
            cx={x1}
            cy={y1}
            r={16}
            fill="transparent"
            stroke="none"
          />
          {/* Outer ring */}
          <circle
            cx={x1}
            cy={y1}
            r={10}
            fill="none"
            stroke={strokeColor}
            strokeWidth={2}
            opacity={opacity}
          />
          {/* Inner dot */}
          <circle
            cx={x1}
            cy={y1}
            r={5}
            fill={strokeColor}
            opacity={opacity}
          />
          {/* Center point for precision */}
          <circle
            cx={x1}
            cy={y1}
            r={2}
            fill="white"
            opacity={opacity * 0.9}
          />
        </g>
      );
    }

    case 'circle': {
      // Circle (ellipse): Drawn from two corners of bounding box
      // Center is midpoint between start and end
      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;
      const rx = Math.abs(x2 - x1) / 2;
      const ry = Math.abs(y2 - y1) / 2;

      // Don't render if too small
      if (rx < 2 && ry < 2) return null;

      return (
        <g key={id} className="chart-drawing chart-drawing-circle">
          {/* Invisible hit area - catches hover events on entire shape */}
          <ellipse
            cx={cx}
            cy={cy}
            rx={rx}
            ry={ry}
            fill="transparent"
            stroke="transparent"
            strokeWidth={Math.max(strokeWidth + 8, 10)}
          />
          {/* Visible ellipse */}
          <ellipse
            cx={cx}
            cy={cy}
            rx={rx}
            ry={ry}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
        </g>
      );
    }

    case 'arrow': {
      // Arrow: Line with arrowhead at the end
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headLength = 12;

      // Calculate arrowhead points
      const headAngle1 = angle - Math.PI / 6;
      const headAngle2 = angle + Math.PI / 6;

      const headX1 = x2 - headLength * Math.cos(headAngle1);
      const headY1 = y2 - headLength * Math.sin(headAngle1);
      const headX2 = x2 - headLength * Math.cos(headAngle2);
      const headY2 = y2 - headLength * Math.sin(headAngle2);

      return (
        <g key={id} className="chart-drawing chart-drawing-arrow">
          {/* Invisible hit area - thicker line for easier hover detection */}
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="transparent"
            strokeWidth={12}
          />
          {/* Main line */}
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
          {/* Arrowhead */}
          <polygon
            points={`${x2},${y2} ${headX1},${headY1} ${headX2},${headY2}`}
            fill={strokeColor}
            opacity={opacity}
          />
        </g>
      );
    }

    case 'rectangle': {
      // Rectangle: Drawn from two corners
      const rectX = Math.min(x1, x2);
      const rectY = Math.min(y1, y2);
      const rectWidth = Math.abs(x2 - x1);
      const rectHeight = Math.abs(y2 - y1);

      // Don't render if too small
      if (rectWidth < 2 && rectHeight < 2) return null;

      return (
        <g key={id} className="chart-drawing chart-drawing-rectangle">
          {/* Invisible hit area - catches hover events on entire shape */}
          <rect
            x={rectX}
            y={rectY}
            width={rectWidth}
            height={rectHeight}
            fill="transparent"
            stroke="none"
          />
          {/* Visible rectangle */}
          <rect
            x={rectX}
            y={rectY}
            width={rectWidth}
            height={rectHeight}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
            rx={2}
            ry={2}
          />
        </g>
      );
    }

    case 'line': {
      // Line: Simple line from start to end
      return (
        <g key={id} className="chart-drawing chart-drawing-line">
          {/* Invisible hit area - thicker line for easier hover detection */}
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="transparent"
            strokeWidth={12}
          />
          {/* Visible line */}
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
        </g>
      );
    }

    case 'text': {
      // Text: Text annotation at a point
      const { text } = drawing;
      if (!text) return null;

      // Background padding for readability
      const padding = 4;
      const fontSize = 12;
      // Estimate text width (approximate, since SVG text measurement is complex)
      const estimatedWidth = text.length * fontSize * 0.6;
      const textHeight = fontSize + padding * 2;

      return (
        <g key={id} className="chart-drawing chart-drawing-text">
          {/* Background rectangle for readability */}
          <rect
            x={x1 - padding}
            y={y1 - fontSize - padding}
            width={estimatedWidth + padding * 2}
            height={textHeight}
            fill="rgba(0, 0, 0, 0.7)"
            rx={3}
            ry={3}
            opacity={opacity}
          />
          {/* Text content */}
          <text
            x={x1}
            y={y1 - padding}
            fill={strokeColor}
            fontSize={fontSize}
            fontFamily="sans-serif"
            opacity={opacity}
          >
            {text}
          </text>
        </g>
      );
    }

    default:
      return null;
  }
};

/**
 * Get the center/anchor point of a drawing for positioning the delete button.
 *
 * @param {Object} drawing - Drawing object
 * @param {Function} xScale - X-axis scale
 * @param {Function} yScale - Y-axis scale
 * @returns {Object} { x, y } pixel coordinates for delete button
 */
const getDrawingCenter = (drawing, xScale, yScale) => {
  const { type, start, end } = drawing;
  const x1 = xScale(start.timestamp);
  const y1 = yScale(start.value);
  const x2 = xScale(end.timestamp);
  const y2 = yScale(end.value);

  switch (type) {
    case 'pin':
      // Pin: top-right of the pin
      return { x: x1 + 12, y: y1 - 12 };

    case 'circle':
    case 'rectangle':
      // Shapes: top-right corner
      return { x: Math.max(x1, x2) + 8, y: Math.min(y1, y2) - 8 };

    case 'arrow':
    case 'line':
      // Lines: near the end point
      return { x: x2 + 8, y: y2 - 8 };

    case 'text':
      // Text: top-right of the text
      return { x: x1 + 8, y: y1 - 20 };

    default:
      return { x: (x1 + x2) / 2, y: (y1 + y2) / 2 - 12 };
  }
};

/**
 * Delete button component that appears when hovering over a drawing.
 *
 * IMPORTANT: This button needs pointer-events: auto because the parent
 * draw layer has pointer-events: none (Recharts intercepts events).
 * The button must be clickable even though the parent layer isn't.
 */
const DeleteButton = ({ x, y, onClick }) => (
  <g
    className="drawing-delete-btn"
    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    {/* Background circle */}
    <circle
      cx={x}
      cy={y}
      r={10}
      fill="rgba(239, 68, 68, 0.9)"
      stroke="rgba(255, 255, 255, 0.3)"
      strokeWidth={1}
    />
    {/* X icon */}
    <path
      d={`M ${x - 4} ${y - 4} L ${x + 4} ${y + 4} M ${x + 4} ${y - 4} L ${x - 4} ${y + 4}`}
      stroke="white"
      strokeWidth={2}
      strokeLinecap="round"
    />
  </g>
);

/**
 * ChartDrawLayer - SVG layer for rendering chart drawings.
 *
 * This component is rendered inside a Recharts <Customized> component,
 * which provides access to the chart's axis scales.
 *
 * HOVER DETECTION (2026-02-04):
 * - SVG hover events don't work inside Recharts Customized components
 * - Recharts intercepts mouse events at the chart level
 * - Hover is detected via pixel coordinates in the parent component
 * - hoveredDrawingId is passed as a prop, not managed internally
 *
 * @param {Object} props - Recharts Customized component props
 * @param {Object} props.xAxisMap - X-axis configuration map from Recharts
 * @param {Object} props.yAxisMap - Y-axis configuration map from Recharts
 * @param {Array} props.drawings - Array of completed drawing objects
 * @param {Object|null} props.currentDrawing - In-progress drawing (preview)
 * @param {boolean} props.isDrawMode - Whether draw mode is active
 * @param {Function} props.onRemoveDrawing - Callback to remove a drawing by ID
 * @param {string|null} props.hoveredDrawingId - ID of the currently hovered drawing (from parent)
 */
export function ChartDrawLayer({
  xAxisMap,
  yAxisMap,
  drawings = [],
  currentDrawing = null,
  isDrawMode = false,
  onRemoveDrawing = null,
  hoveredDrawingId = null, // Passed from parent - SVG hover doesn't work in Recharts
}) {
  // Get the first X and Y axis (typically the only ones)
  const xAxis = xAxisMap ? Object.values(xAxisMap)[0] : null;
  const yAxis = yAxisMap ? Object.values(yAxisMap)[0] : null;

  // Can't render without scales
  if (!xAxis?.scale || !yAxis?.scale) {
    return null;
  }

  const xScale = xAxis.scale;
  const yScale = yAxis.scale;

  // No drawings to render
  if (drawings.length === 0 && !currentDrawing) {
    return null;
  }

  // Handle delete click
  const handleDelete = (drawingId) => {
    if (onRemoveDrawing) {
      onRemoveDrawing(drawingId);
    }
  };

  return (
    <g
      className="chart-draw-layer"
      // Pointer events are now handled at the chart level, not in SVG
      style={{ pointerEvents: 'none' }}
    >
      {/* Render completed drawings */}
      {drawings.map(drawing => {
        const isHovered = hoveredDrawingId === drawing.id;
        const deletePos = getDrawingCenter(drawing, xScale, yScale);

        return (
          <g
            key={drawing.id}
            className={`chart-drawing-wrapper ${isHovered ? 'hovered' : ''}`}
          >
            {/* The drawing itself */}
            {renderDrawing(drawing, xScale, yScale, false)}

            {/* Delete button - show when hovered (works in draw mode too) */}
            {isHovered && onRemoveDrawing && (
              <DeleteButton
                x={deletePos.x}
                y={deletePos.y}
                onClick={() => handleDelete(drawing.id)}
              />
            )}
          </g>
        );
      })}

      {/* Render in-progress drawing as preview */}
      {currentDrawing && renderDrawing(currentDrawing, xScale, yScale, true)}
    </g>
  );
}

export default ChartDrawLayer;
